import { useMemo } from 'react';

import type { VariableDefinition } from '@repo/videa-components/src/ui/variable-textarea/types';
import type { MicrophonePermissionOutcome } from '@repo/videa-notes';

import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';

import type {
  VideaNotesCoreContextValue,
  VideaNotesPatient,
} from '../context/VideaNotesCoreContext';
import type { VideaNotesNoteResultContextValue } from '../context/VideaNotesNoteResultContext';
import type { VideaNotesRecordingContextValue } from '../context/VideaNotesRecordingContext';
import type {
  VideaNotesProcessingError,
  VideaNotesUIState,
  VoiceNoteTemplateOption,
  VoiceNotesSource,
} from '../types';

// ============================================================================
// Core Context Value Hook
// ============================================================================

interface UseCoreContextValueParams {
  uiStatus: VideaNotesUIState;
  voiceNotesSource: VoiceNotesSource;
  patientName: string;
  patient: VideaNotesPatient | undefined;
  standalonePatientVisitId: string | undefined;
  isVoiceNotesConsentEnabled: boolean;
  processingError: VideaNotesProcessingError | undefined;
  onCloseSheet: () => void;
  onPatientCreated: (visitData: {
    id: string;
    firstName: string;
    lastName: string | null;
    sourcePatientId: string | null;
    dateOfBirth: Date | null;
    appointmentDate: Date;
    appointmentStartTime: string;
  }) => void;
}

export const useCoreContextValue = ({
  uiStatus,
  voiceNotesSource,
  patientName,
  patient,
  standalonePatientVisitId,
  isVoiceNotesConsentEnabled,
  processingError,
  onCloseSheet,
  onPatientCreated,
}: UseCoreContextValueParams): VideaNotesCoreContextValue => {
  return useMemo(
    () => ({
      uiStatus,
      voiceNotesSource,
      patientName,
      patient,
      standalonePatientVisitId,
      isVoiceNotesConsentEnabled,
      processingError,
      onCloseSheet,
      onPatientCreated,
    }),
    [
      uiStatus,
      voiceNotesSource,
      patientName,
      patient,
      standalonePatientVisitId,
      isVoiceNotesConsentEnabled,
      processingError,
      onCloseSheet,
      onPatientCreated,
    ],
  );
};

// ============================================================================
// Recording Context Value Hook
// ============================================================================

interface UseRecordingContextValueParams {
  recorderStatus: string;
  mediaBlobUrl: string | undefined;
  templateOptions: VoiceNoteTemplateOption[];
  selectedTemplate: string | undefined;
  selectedTemplateName: string | null;
  selectedTemplateUserInstructions: string | null;
  supportedLanguages: string[];
  selectedLanguage: string;
  onSelectedLanguage: (language: string) => void;
  isAudioUploadEnabled: boolean;
  showDevTools: boolean;
  onSelectedTemplate: (templateId: string | undefined) => void;
  onMonitoringStreamChange: (stream: MediaStream | null) => void;
  onCancelRecording: () => void;
  onCancelRecordingConfirmation: () => void;
  onPauseRecording: () => void;
  onResumeRecording: () => void;
  onStartRecording: () => void;
  onStartProviderOnlyRecording: () => void;
  onGenerateNote: (uploadedFile?: File, blobUrl?: string) => void;
  onPatientConsentDenied: () => void;
  onPermissionOutcome?: (outcome: MicrophonePermissionOutcome) => void;
}

export const useRecordingContextValue = ({
  recorderStatus,
  mediaBlobUrl,
  templateOptions,
  selectedTemplate,
  selectedTemplateName,
  selectedTemplateUserInstructions,
  supportedLanguages,
  selectedLanguage,
  onSelectedLanguage,
  isAudioUploadEnabled,
  showDevTools,
  onSelectedTemplate,
  onMonitoringStreamChange,
  onCancelRecording,
  onCancelRecordingConfirmation,
  onPauseRecording,
  onResumeRecording,
  onStartRecording,
  onStartProviderOnlyRecording,
  onGenerateNote,
  onPatientConsentDenied,
  onPermissionOutcome,
}: UseRecordingContextValueParams): VideaNotesRecordingContextValue => {
  return useMemo(
    () => ({
      recorderStatus,
      mediaBlobUrl,
      templateOptions,
      selectedTemplate,
      selectedTemplateName,
      selectedTemplateUserInstructions,
      supportedLanguages,
      selectedLanguage,
      onSelectedLanguage,
      isAudioUploadEnabled,
      showDevTools,
      onSelectedTemplate,
      onMonitoringStreamChange,
      onCancelRecording,
      onCancelRecordingConfirmation,
      onPauseRecording,
      onResumeRecording,
      onStartRecording,
      onStartProviderOnlyRecording,
      onGenerateNote,
      onPatientConsentDenied,
      onPermissionOutcome,
    }),
    [
      recorderStatus,
      mediaBlobUrl,
      templateOptions,
      selectedTemplate,
      selectedTemplateName,
      selectedTemplateUserInstructions,
      supportedLanguages,
      selectedLanguage,
      onSelectedLanguage,
      isAudioUploadEnabled,
      showDevTools,
      onSelectedTemplate,
      onMonitoringStreamChange,
      onCancelRecording,
      onCancelRecordingConfirmation,
      onPauseRecording,
      onResumeRecording,
      onStartRecording,
      onStartProviderOnlyRecording,
      onGenerateNote,
      onPatientConsentDenied,
      onPermissionOutcome,
    ],
  );
};

// ============================================================================
// Note Result Context Value Hook
// ============================================================================

interface UseNoteResultContextValueParams {
  clinicalNotesData: ClinicalNote[];
  selectedNote: ClinicalNote | undefined;
  noteContent: string;
  patientConsentBlurb: string | undefined;
  isNoteReadOnly: boolean;
  isNoteSavedToPMS: boolean;
  isSavingToPMSInProgress: boolean;
  isSavingNoteUpdate: boolean;
  isRetryingWriteback: boolean;
  hasWritebackEnabled: boolean;
  hasBeenEdited: boolean;
  showCopyTooltip: boolean;
  variableDefinitions: VariableDefinition[];
  onNoteChange: (value: string) => void;
  onNoteClick: () => void;
  onSelectedNote: (noteId: string | undefined) => void;
  onCopyText: (text: string) => void;
  onCreateAdditionalNote: () => void;
  onSaveNoteToPMS: () => void;
  onRetryWritebackToPMS: () => void;
  onResetNote: () => void;
  onVariableValueChange: (varName: string, newValue: string) => void;
}

export const useNoteResultContextValue = ({
  clinicalNotesData,
  selectedNote,
  noteContent,
  patientConsentBlurb,
  isNoteReadOnly,
  isNoteSavedToPMS,
  isSavingToPMSInProgress,
  isSavingNoteUpdate,
  isRetryingWriteback,
  hasWritebackEnabled,
  hasBeenEdited,
  showCopyTooltip,
  variableDefinitions,
  onNoteChange,
  onNoteClick,
  onSelectedNote,
  onCopyText,
  onCreateAdditionalNote,
  onSaveNoteToPMS,
  onRetryWritebackToPMS,
  onResetNote,
  onVariableValueChange,
}: UseNoteResultContextValueParams): VideaNotesNoteResultContextValue => {
  return useMemo(
    () => ({
      clinicalNotesData,
      selectedNote,
      noteContent,
      patientConsentBlurb,
      isNoteReadOnly,
      isNoteSavedToPMS,
      isSavingToPMSInProgress,
      isSavingNoteUpdate,
      isRetryingWriteback,
      hasWritebackEnabled,
      hasBeenEdited,
      showCopyTooltip,
      variableDefinitions,
      onNoteChange,
      onNoteClick,
      onSelectedNote,
      onCopyText,
      onCreateAdditionalNote,
      onSaveNoteToPMS,
      onRetryWritebackToPMS,
      onResetNote,
      onVariableValueChange,
    }),
    [
      clinicalNotesData,
      selectedNote,
      noteContent,
      patientConsentBlurb,
      isNoteReadOnly,
      isNoteSavedToPMS,
      isSavingToPMSInProgress,
      isSavingNoteUpdate,
      isRetryingWriteback,
      hasWritebackEnabled,
      hasBeenEdited,
      showCopyTooltip,
      variableDefinitions,
      onNoteChange,
      onNoteClick,
      onSelectedNote,
      onCopyText,
      onCreateAdditionalNote,
      onSaveNoteToPMS,
      onRetryWritebackToPMS,
      onResetNote,
      onVariableValueChange,
    ],
  );
};
